import { formatFeetInches } from "./feetInches";
import { parseDOB } from "./age";

/**
 * Strips HTML from report content to produce human-readable raw text.
 * Preserves newlines for block parsing.
 * @param {string} html
 * @returns {string}
 */
export function htmlToRawText(html) {
  if (!html || typeof html !== "string") return "";
  if (typeof document === "undefined") {
    return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const text = tmp.textContent || tmp.innerText || "";
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n\s*\n/g, "\n\n")
    .trim();
}

/**
 * Try to extract first match (including decimals, feet/inches).
 * @param {string} text
 * @param {RegExp} regex - should have one capture group for the value
 * @returns {string}
 */
function extractFirst(text, regex) {
  const m = text.match(regex);
  return m && m[1] ? String(m[1]).trim() : "";
}

/** Max length for event name (avoid pulling raw block into event column). */
const MAX_EVENT_LENGTH = 120;

/** Regex to detect measurement-like content (not a real event name). */
const MEASUREMENT_CONTENT_RE = /height|weight|wingspan|standing\s*reach|hand\s*(length|width)|vertical|lane\s*agility|shuttle|sprint|body\s*fat|bf\s*%|wingspan\s*differential/i;

/**
 * Sanitize a candidate event value so we don't put raw content in the event column.
 * Reject if it looks like measurement data; optionally truncate if too long.
 * @param {string} val
 * @param {boolean} allowTruncate - if true, truncate long values instead of rejecting
 * @returns {string}
 */
function sanitizeEventValue(val, allowTruncate = false) {
  if (val == null || typeof val !== "string") return "";
  const t = val.trim();
  if (MEASUREMENT_CONTENT_RE.test(t)) return "";
  if (t.length > MAX_EVENT_LENGTH) return allowTruncate ? t.slice(0, MAX_EVENT_LENGTH).trim() : "";
  return t;
}

/**
 * Normalize height-style value: 6' 3.2", 6'3.2", 6'4" -> 6'3.2", 6'4"
 * @param {string} raw
 * @returns {string}
 */
function normalizeHeight(raw) {
  if (!raw || !raw.trim()) return "";
  const s = raw
    .replace(/\s*ft\s*/gi, "'")
    .replace(/\s*in\s*/gi, '"')
    .replace(/\s+/g, "")
    .replace(/'/g, "'")
    .trim();
  return formatFeetInches(s);
}

/**
 * Extract height-style value (feet'inches" or feet'inches).
 * Handles: 5'11.25", 6' 3.2", 6' 4", 6'4.4"
 */
function extractHeight(text, regex) {
  const m = text.match(regex);
  if (!m || !m[1]) return "";
  return normalizeHeight(String(m[1]).trim());
}

/** Measurement label prefixes that indicate a data line, not an event header */
const MEASUREMENT_LABEL_STARTS = [
  "height", "weight", "wingspan", "standing", "hand", "vertical", "vert",
  "max vert", "lane", "reaction", "reactive", "3/4", "sprint", "shuttle",
  "bf%", "body fat", "wingspan differential", "(+/-)", "standing vert",
  "standing vert reach", "max vert reach", "lane agility"
];

function isMeasurementLabel(line) {
  const lower = (line || "").toLowerCase().replace(/\s*:\s*$/, "").trim();
  return MEASUREMENT_LABEL_STARTS.some((start) => lower.startsWith(start));
}

/**
 * Split raw text into blocks by event headers (e.g. "NBA G League Elite Camp:", "NBA Combine:", "Portsmouth Invitational Tournament:").
 * Event header = line ending with ":" followed by newline, not a measurement label.
 * Returns array of { eventName, blockText, rawBlock }.
 */
function splitIntoBlocks(text) {
  if (!text || !text.trim()) return [{ eventName: "", blockText: text, rawBlock: text || "" }];
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    const nextIsBlank = i + 1 < lines.length && !lines[i + 1].trim();
    const looksLikeEventHeader =
      trimmed.endsWith(":") &&
      trimmed.length > 4 &&
      !isMeasurementLabel(trimmed) &&
      /^[A-Za-z0-9]/.test(trimmed) &&
      (nextIsBlank || !isMeasurementLabel(lines[i + 1]?.trim() || ""));
    if (looksLikeEventHeader) {
      const headerLineIndex = i;
      const eventName = trimmed.replace(/\s*:\s*$/, "").trim();
      i += 1;
      while (i < lines.length && !lines[i].trim()) i += 1;
      const blockStart = i;
      while (i < lines.length) {
        const l = lines[i].trim();
        const couldBeNextHeader = l.endsWith(":") && l.length > 4 && !isMeasurementLabel(l) && /^[A-Za-z0-9]/.test(l);
        if (couldBeNextHeader) break;
        i += 1;
      }
      const blockText = lines.slice(blockStart, i).join("\n").trim();
      const rawBlock = lines.slice(headerLineIndex, i).join("\n").trim();
      blocks.push({ eventName, blockText, rawBlock });
      continue;
    }
    i += 1;
  }
  if (blocks.length === 0) return [{ eventName: "", blockText: text, rawBlock: text }];
  return blocks;
}

/**
 * Parse event date string (MM/DD/YY, MM/DD/YYYY, MM-DD-YY) to Date.
 * @returns {Date|null}
 */
function parseEventDateStr(str) {
  if (!str || !String(str).trim()) return null;
  const s = String(str).trim();
  const sep = s.includes("/") ? "/" : "-";
  const parts = s.split(sep);
  if (parts.length !== 3) return null;
  const mm = parseInt(parts[0], 10);
  const dd = parseInt(parts[1], 10);
  const yy = parseInt(parts[2], 10);
  const year = parts[2].length === 2 ? (yy >= 0 && yy <= 99 ? 2000 + yy : yy) : yy;
  const d = new Date(year, mm - 1, dd);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Compute age at event date in decimal years (DOB and event date required).
 * @returns {string} e.g. "18.5" or ""
 */
function ageAtEventDecimal(dobDate, eventDate) {
  if (!dobDate || !eventDate || eventDate < dobDate) return "";
  const msPerYear = 365.25 * 24 * 60 * 60 * 1000;
  const ageYears = (eventDate - dobDate) / msPerYear;
  return ageYears >= 0 ? ageYears.toFixed(1) : "";
}

/**
 * Parse one block of text into measurement fields.
 * Covers Format 1–6 and variants (Height w/o shoes, Height Without Shoes, BF%, Vertical Jump, Max Vert, etc.).
 */
function parseOneBlock(blockText, eventName, report, playerName, blockRawData, fallbackEvent, fallbackEventDate, playerDOB) {
  const text = blockText;
  const lower = text.toLowerCase();

  const formatReportDate = (d) => {
    if (!d) return "";
    const date = d.toDate ? d.toDate() : new Date(d);
    if (isNaN(date.getTime())) return "";
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  const eventDateStr =
    extractFirst(text, /\b(?:event\s*date|date\s*of\s*event|measured?\s*on)[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i) ||
    extractFirst(text, /\b(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})\b/) ||
    fallbackEventDate ||
    formatReportDate(report.reportDate) ||
    "";

  const eventDate =
    parseEventDateStr(eventDateStr) ||
    (report.reportDate ? (report.reportDate.toDate ? report.reportDate.toDate() : new Date(report.reportDate)) : null);

  const extractedEvent = extractFirst(text, /\b(?:event|camp|combine)[:\s]*([^\n]+)/i);
  const fallbackEventVal = fallbackEvent || (report.reportPreview ? String(report.reportPreview).trim() : "") || "";
  const event =
    eventName ||
    sanitizeEventValue(extractedEvent) ||
    sanitizeEventValue(fallbackEventVal, true) ||
    "";

  const dobDate = playerDOB ? parseDOB(playerDOB) : null;
  const ageFromDOB = dobDate && eventDate ? ageAtEventDecimal(dobDate, eventDate) : "";
  const ageFromText = extractFirst(
    text,
    /\b(?:age\s*at\s*event|age)[:\s]*(\d+(?:\.\d+)?)\s*(?:yrs?\.?|years?)?/i
  );
  const age = ageFromDOB || ageFromText || "";

  // Weight: "Weight: 202.2 lbs", "Weight: 187 lbs.", pipe-separated "Weight: 167.8"
  const weight = extractFirst(
    text,
    /\bweight[:\s]*(\d+(?:\.\d+)?)\s*(?:lbs?\.?)?/i
  );
  const weightFormatted = weight ? `${weight} lbs.` : "";

  // Body fat: "BF%: 5.7", "Body Fat: 12.5%"
  const bodyFat = extractFirst(
    text,
    /\b(?:body\s*fat|bf\s*%?|bodyfat)[:\s]*(\d+(?:\.\d+)?)\s*%?/i
  );
  const bodyFatFormatted = bodyFat ? (bodyFat.includes("%") ? bodyFat : `${bodyFat}%`) : "";

  // Height w/ shoes: "Height w/ shoes: 6'0.25"", "Height w/ Shoes: 6' 4.4"", "Height With Shoes: 6' 5""
  const heightShoes =
    extractHeight(text, /\bheight\s*w\/?\s*shoes?[:\s]*([\d'\s\."]+)/i) ||
    extractHeight(text, /\bheight\s*with\s*shoes?[:\s]*([\d'\s\."]+)/i) ||
    extractHeight(text, /\bheight\s*w\/\s*shoes?[:\s]*([\d'\s\."]+)/i);

  // Height w/o shoes: "Height w/o shoes: 5'11.25"", "Height Without Shoes: 6' 3.2""
  const heightNone =
    extractHeight(text, /\bheight\s*w\/o?\s*shoes?[:\s]*([\d'\s\."]+)/i) ||
    extractHeight(text, /\bheight\s*without\s*shoes?[:\s]*([\d'\s\."]+)/i) ||
    extractHeight(text, /\bheight\s*w\/o\s*shoes?[:\s]*([\d'\s\."]+)/i);

  // Wingspan: "Wingspan: 6'2.5"", "Wingspan: 6' 4""
  const wingspan = extractHeight(
    text,
    /\bwingspan[:\s]*([\d'\s\."]+)/i
  );

  // Standing Reach: "Standing Reach: 7'10"", "Standing Reach: 8' 2""
  const standingReach = extractHeight(
    text,
    /\bstanding\s*reach[:\s]*([\d'\s\."]+)/i
  );

  // Hand Length / Hand Width: "Hand Length: 8"", "Hand Length: 9.5""
  const handL =
    extractFirst(text, /\bhand\s*length[:\s]*(\d+(?:\.\d+)?)\s*"?/i) ||
    extractFirst(text, /\bhand\s*length[:\s]*(\d+(?:\.\d+)?)\s*in/i) ||
    extractFirst(text, /\bhand\s*L[:\s]*(\d+(?:\.\d+)?)/i);
  const handW =
    extractFirst(text, /\bhand\s*width[:\s]*(\d+(?:\.\d+)?)\s*"?/i) ||
    extractFirst(text, /\bhand\s*width[:\s]*(\d+(?:\.\d+)?)\s*in/i) ||
    extractFirst(text, /\bhand\s*W[:\s]*(\d+(?:\.\d+)?)/i);

  // Vertical: "Vertical Jump (inches): 20.54", "Vertical Jump: 26.5"", "Max Vert: 32.5"", "Standing Vert: 29"", "Vertical: 26""
  const vertMax =
    extractFirst(text, /\bmax\s*vert[:\s]*(\d+(?:\.\d+)?)\s*"?/i) ||
    extractFirst(text, /\bvertical\s*jump\s*max[:\s]*(\d+(?:\.\d+)?)/i) ||
    extractFirst(text, /\bmax\s*vert[:\s]*(\d+(?:\.\d+)?)\s*in/i);
  const vertNoStep =
    extractFirst(text, /\b(?:vertical\s*jump|standing\s*vert|vertical)[:\s]*(\d+(?:\.\d+)?)\s*"?/i) ||
    extractFirst(text, /\bvertical\s*jump\s*\(inches\)[:\s]*(\d+(?:\.\d+)?)/i) ||
    extractFirst(text, /\bvertical\s*jump[:\s]*(\d+(?:\.\d+)?)\s*in/i) ||
    extractFirst(text, /\bno\s*step\s*vert[:\s]*(\d+(?:\.\d+)?)/i);

  // Lane Agility: "Lane Agility Drill (seconds): 12.27", "Lane Agility: 2.88 sec", "Lane Agility: 12.72"
  const laneAgility =
    extractFirst(text, /\blane\s*agility\s*drill\s*\(seconds\)[:\s]*(\d+(?:\.\d+)?)/i) ||
    extractFirst(text, /\blane\s*agility[:\s]*(\d+(?:\.\d+)?)\s*(?:sec\.?)?/i);

  // Shuttle: "Reaction Shuttle: 11.5 sec", "Reactive Shuttle: 3.22", "Reaction Shuttle: 11.138 sec"
  const shuttle =
    extractFirst(text, /\b(?:reaction|reactive)\s*shuttle[:\s]*(\d+(?:\.\d+)?)\s*(?:sec\.?)?/i) ||
    extractFirst(text, /\bshuttle[:\s]*(\d+(?:\.\d+)?)\s*(?:sec\.?)?/i);

  // Sprint: "3/4 Court Sprint: 3.22 sec", "3/4 Court Sprint: 3.29"
  const sprint =
    extractFirst(text, /\b3\/4\s*(?:court\s*)?sprint[:\s]*(\d+(?:\.\d+)?)\s*(?:sec\.?)?/i) ||
    extractFirst(text, /\bsprint[:\s]*(\d+(?:\.\d+)?)\s*(?:sec\.?)?/i);

  return {
    playerProfileID: report.playerProfile || "",
    playerName: playerName || "",
    age,
    event,
    eventDate: eventDateStr,
    bodyFat: bodyFatFormatted,
    handL,
    handW,
    heightNone,
    heightShoes,
    laneAgility,
    shuttle,
    sprint,
    standingReach,
    vertMax,
    vertNoStep,
    weight: weightFormatted,
    wingspan,
    rawData: blockRawData,
  };
}

/**
 * Parse report content; supports pipe-separated single line (Format 6) and multiple event blocks (Format 4).
 * Returns an array of parsed objects (one per event block, or one for whole content).
 *
 * @param {Object} report - { reportContent, reportDate, reportPreview, playerProfile, ... }
 * @param {string} playerName - resolved player name
 * @param {*} playerDOB - optional DOB from player-profile (Firestore Timestamp, { seconds }, or date string)
 * @returns {Array<{ playerProfileID, playerName, age, event, eventDate, bodyFat, handL, handW, heightNone, heightShoes, laneAgility, shuttle, sprint, standingReach, vertMax, vertNoStep, weight, wingspan, rawData }>}
 */
export function parseReportForMeasurements(report, playerName = "", playerDOB = null) {
  const rawData = htmlToRawText(report.reportContent || "");
  const fallbackEvent = report.reportPreview ? String(report.reportPreview).trim() : "";
  const fallbackEventDate = report.reportDate
    ? (() => {
        const d = report.reportDate.toDate ? report.reportDate.toDate() : new Date(report.reportDate);
        if (isNaN(d.getTime())) return "";
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        return `${mm}/${dd}/${yy}`;
      })()
    : "";

  // Format 6: single line with pipe-separated "Label: value | Label: value"
  const pipeSeparated = rawData.includes("|") && rawData.split("\n").filter((l) => l.trim()).length <= 2;
  if (pipeSeparated) {
    const oneBlock = rawData.replace(/\|/g, "\n");
    const row = parseOneBlock(oneBlock, "", report, playerName, rawData, fallbackEvent, fallbackEventDate, playerDOB);
    return [row];
  }

  const blocks = splitIntoBlocks(rawData);
  return blocks.map(({ eventName, blockText, rawBlock }) =>
    parseOneBlock(blockText, eventName, report, playerName, rawBlock, fallbackEvent, fallbackEventDate, playerDOB)
  );
}
